<template>
  <form class="policy-edit-form" @submit.prevent="handleUpdate">
    <section class="policy-edit-section">
      <div class="policy-edit-head">
        <div>
          <span>Plan Shelf</span>
          <h5>Update Plan</h5>
          <p>Review the plan name, description, premium values, and optional benefit spreadsheet.</p>
        </div>
        <i
          class="material-icons policy-edit-ready"
          v-if="policyName && policyDesc && premiumIndividual && premiumFamily"
        >
          check_circle
        </i>
      </div>

      <div class="policy-edit-grid">
        <div
          class="policy-edit-field policy-edit-wide"
          :class="{ 'has-error': error && !policyName }"
        >
          <div class="input-field">
            <input type="text" id="policyName" v-model="policyName" />
            <label class="active" for="policyName">Plan Name</label>
          </div>
        </div>

        <div
          class="policy-edit-field policy-edit-wide"
          :class="{ 'has-error': error && !policyDesc }"
        >
          <div class="input-field">
            <input type="text" id="policyDesc" v-model="policyDesc" />
            <label class="active" for="policyDesc">Description</label>
          </div>
        </div>

        <div
          class="policy-edit-field"
          :class="{ 'has-error': error && !premiumIndividual }"
        >
          <i class="material-icons">person</i>
          <div class="input-field">
            <input
              type="number"
              id="premiumIndividual"
              v-model="premiumIndividual"
              class="validate"
              required
            />
            <label class="active" for="premiumIndividual">Individual Premium</label>
          </div>
        </div>

        <div
          class="policy-edit-field"
          :class="{ 'has-error': error && !premiumFamily }"
        >
          <i class="material-icons">family_restroom</i>
          <div class="input-field">
            <input
              type="number"
              id="premiumFamily"
              v-model="premiumFamily"
              class="validate"
              required
            />
            <label class="active" for="premiumFamily">Family Premium</label>
          </div>
        </div>
      </div>

      <div class="policy-upload-card">
        <div class="policy-upload-copy">
          <span>Benefits</span>
          <strong>Replace Benefit Sheet</strong>
          <p>Upload a new XLSX or XLS file only when the benefit lines need to change.</p>
        </div>

        <div class="file-field input-field policy-file-field">
          <div class="btn policy-file-btn" :class="{ disabled: isSubmitting }">
            <span>Choose File</span>
            <input type="file" @change="handleFile" />
          </div>
          <div class="file-path-wrapper">
            <input class="file-path" v-model="inputFile" type="text" placeholder="No file selected" />
          </div>
          <p class="red-text" v-if="fileError">
            {{ fileError }}
          </p>
        </div>
      </div>

      <div class="policy-edit-actions">
        <button
          type="button"
          class="waves-effect waves-light btn-large policy-cancel-btn"
          :class="{
            disabled: isSubmitting,
          }"
          @click="handleBack"
        >
          Cancel
        </button>
        <button
          class="waves-effect waves-light btn-large"
          :class="{
            disabled: isSubmitting,
          }"
        >
          <i class="material-icons white-text left">check_circle</i>
          {{ isSubmitting ? `Updating (${uploadProgress})` : "Update Plan" }}
        </button>
      </div>
    </section>

    <section class="policy-edit-error" v-if="error || uploadError">
      <i class="material-icons">error_outline</i>
      <span>{{ uploadError || "Please complete the required plan fields before updating." }}</span>
    </section>
  </form>
</template>

<script>
import { ref, onMounted, watchEffect } from "vue";
import useStorage from "@/composables/useStorage";
import postItem from "@/composables/postItem";

export default {
  name: "PolicyEditOnboardComponent",
  props: ["policy"],
  emits: ["complete"],
  setup(props, context) {
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const isSubmitting = ref(false);
    const error = ref(null);
    const next = ref(false);
    const policyName = ref(null);
    const policyDesc = ref(null);
    const premiumIndividual = ref(null);
    const premiumFamily = ref(null);
    const checkIndividual = ref(null);
    const checkFamily = ref(null);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // starter
    onMounted(() => {
      // get policies
      // null all
      policyName.value = props.policy.name;
      policyDesc.value = props.policy.description;
      premiumIndividual.value = props.policy.premiumIndividual;
      premiumFamily.value = props.policy.premiumFamily;

      // checkers
      checkIndividual.value = props.policy.premiumIndividual;
      checkFamily.value = props.policy.premiumFamily;
    });

    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    // handle add
    const handleUpdate = async () => {
      if (premiumIndividual.value && premiumFamily.value && policyDesc.value) {
        // Start Uploading
        isSubmitting.value = true;

        // initiate for update
        let plan = null;
          let individual = null;
          let family = null;
          let scope = null;

          // new plan
          plan = {
            id: props.policy.id,
            name: policyName.value,
            description: policyDesc.value,
          };

          // update individual premium
          if (checkIndividual.value != premiumIndividual.value) {
            individual = {
              plan_id: null, // update this on server
              amount: premiumIndividual.value,
              type: "i",
            };
          }

          // update family premium
          if (checkFamily.value != premiumFamily.value) {
            family = {
              plan_id: null, // update this on serve
              amount: premiumFamily.value,
              type: "f",
            };
          }

          // scope
          scope = 0; // get proper client id

        // File (Benefits)
        if (file.value) {
          // Start Uploading
          uploadError.value = null;

          // Cloud Function
          const fx = "planLoader";

          // round up
          const cloudInput = {
            plan: plan,
            individual: individual,
            family: family,
            scope: scope,
            action: "update",
          };

          // upload
          uploadFile(file.value, fx, cloudInput);
        } else {
          // update without File
          // (without File)
         
          // Start Uploading
          uploadProgress.value = "100%";

          // round up
          const newVal = {
            plan: plan,
            individual: individual,
            family: family,
            scope: scope,
            action: "update",
            benefits: [], // empty array (no Benefit change required)
          };

          // post item
          const { res, err } = await postItem("plan/benefits/bulk", newVal);

          if (res.value) {
            // success
            handleNext(res.value.inserted_rows);
          } else {
            error.value = err.value;
            isSubmitting.value = false;
          }
        }
      } else {
        error.value = true;
      }
    };

    // watch upload
    watchEffect(() => {
      if (completed.value) {
        // success
        handleNext(data.value.succ.inserted_rows);
      }
      if (uploadError.value) {
        // restart
        // handleReset();
        // stop
        error.value = uploadError.value;
        isSubmitting.value = false;
      }
    });

    // handle submit
    const handleNext = (total) => {
      // emit new parameters
      context.emit("complete", {
        id: "update",
        complete: true,
        data: {
          id: props.policy.id,
          status: props.policy.status,
          scheme_id: 1,
          premiumIndividual: premiumIndividual.value,
          premiumFamily: premiumFamily.value,
          name: policyName.value,
          description: "Standard Shelf Plan",
          benefitTotal: total,
          benefits: props.policy.benefits,
        },
      });
    };

    // handle submit
    const handleBack = () => {
      // emit new parameters
      context.emit("complete", {
        id: "update",
        complete: false,
        data: null,
      });
    };

    return {
      isSubmitting,
      handleUpdate,
      handleBack,
      handleNext,
      handleFile,
      fileError,
      uploadError,
      file,
      inputFile,
      uploadProgress,
      error,
      next,
      premiumIndividual,
      premiumFamily,
      policyName,
      policyDesc,
    };
  },
};
</script>

<style scoped>
.policy-edit-form {
  display: grid;
  gap: 18px;
}

.policy-edit-section,
.policy-edit-error {
  background: #ffffff;
  border: 1px solid #d3dde1;
  border-radius: 6px;
  overflow: hidden;
}

.policy-edit-head {
  align-items: center;
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 22px 24px;
}

.policy-edit-head span,
.policy-upload-copy span {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.policy-edit-head h5 {
  color: #263238;
  font-size: 1.34rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.policy-edit-head p,
.policy-upload-copy p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px 0 0;
}

.policy-edit-ready {
  align-items: center;
  background: #e8f5e9;
  border-radius: 6px;
  color: #2e7d32;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.policy-edit-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 20px 24px;
}

.policy-edit-wide {
  grid-column: 1 / -1;
}

.policy-edit-field,
.policy-upload-card {
  background: #ffffff;
  border: 1px solid #d3dde1;
  border-radius: 6px;
}

.policy-edit-field {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr);
  padding: 14px 16px 8px;
}

.policy-edit-field > i {
  color: #00897b;
}

.policy-edit-field.has-error,
.policy-upload-card.has-error {
  border-color: #ffcdd2;
}

.policy-edit-field :deep(.input-field),
.policy-file-field {
  margin: 0;
}

.policy-edit-field :deep(input) {
  margin-bottom: 0;
}

.policy-upload-card {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 0.9fr) minmax(280px, 1.1fr);
  margin: 0 24px 22px;
  padding: 18px;
}

.policy-upload-copy strong {
  color: #263238;
  display: block;
  font-size: 1rem;
  font-weight: 800;
}

.policy-file-btn,
.policy-edit-actions .btn-large {
  background: #00897b;
  border-radius: 6px;
  box-shadow: none;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.policy-edit-actions {
  background: #fbfcfc;
  border-top: 1px solid #edf1f3;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 24px;
}

.policy-edit-actions .policy-cancel-btn {
  background: #ffffff;
  border: 1px solid #d3dde1;
  box-shadow: none;
  color: #263238;
}

.policy-edit-error {
  align-items: center;
  color: #b71c1c;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
}

.policy-edit-error span {
  font-weight: 800;
}

@media only screen and (max-width: 992px) {
  .policy-edit-head,
  .policy-edit-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .policy-edit-grid,
  .policy-upload-card {
    grid-template-columns: 1fr;
  }
}
</style>
